import { Injectable, signal } from '@angular/core';
import { icon } from '@fortawesome/fontawesome-svg-core';
import { faPrint } from '@fortawesome/pro-solid-svg-icons';
// @ts-expect-error No type declaration type exists
import domtoimage from 'dom-to-image-more';
// @ts-expect-error No type declaration type exists
import ImageTracer from 'imagetracerjs';
import { LngLatBoundsLike, Map as MapLibreMap, ScaleControl } from 'maplibre-gl';

import {
    MapExportFormatEnum,
    MapExportPrintSizeEnum,
} from '@connect-services/client/components/map-export-modal/map-export-modal.component';

export type MapExportOption = {
    bbox?: LngLatBoundsLike;
    hideAllControls?: boolean;
    hideMarkers?: boolean;
    hidePopups?: boolean;
};

export type ImageInformation = {
    dataUrl: string;
    h: number;
    needCrop?: boolean;
    w: number;
    x?: number;
    y: number;
};

interface SelectedMapExportOption {
    compass: boolean;
    legend: boolean;
    scale: boolean;
}

export const PAPER_SIZE_IN_PIXEL: Record<MapExportPrintSizeEnum, { height: number; width: number }> = {
    a0: { height: 9933, width: 14043 },
    a1: { height: 7016, width: 9933 },
    a2: { height: 4961, width: 7016 },
    a3: { height: 3508, width: 4961 },
    a4: { height: 2480, width: 3508 },
};

const DEFAULT_SVG_OPTION = {
    colorquantcycles: 5,
    colorsampling: 0,
    linefilter: false,
    ltres: 0.01,
    mincolorratio: 0.001,
    numberofcolors: 256,
    pathomit: 0,
    qtres: 0.01,
    roundcoords: 2,
    scale: 1,
    strokewidth: 0,
    viewbox: true,
};

const SHRINK_LEGEND = 0.55;

@Injectable({
    providedIn: 'root',
})
export class MapToImageService {
    public readonly bottomPad = signal(0);
    public button: HTMLButtonElement = document.createElement('button');
    public readonly imgUrl = signal<string | null>(null);
    public readonly isMapExportActive = signal<boolean>(false);
    public readonly isMapExportControlDisabled = signal<boolean>(false);
    public readonly leftPad = signal(0);
    public readonly xLegendPlacement = signal(0);

    public async exportImage(
        map: MapLibreMap,
        bearing: number,
        fileName: string,
        format: MapExportFormatEnum,
        printSize: MapExportPrintSizeEnum,
        selectedOptions: SelectedMapExportOption,
    ): Promise<void> {
        let finalImageUrl = await this.generateFinalImage(map, bearing, format, printSize, selectedOptions);

        if (format === MapExportFormatEnum.SVG) {
            await this.convertPngToSvg(finalImageUrl, fileName);

            return;
        }

        if (format === MapExportFormatEnum.JPEG) {
            finalImageUrl = finalImageUrl.replace(MapExportFormatEnum.PNG, MapExportFormatEnum.JPEG);
        }

        this.downloadImage(finalImageUrl, fileName);
        this.xLegendPlacement.set(0);
    }

    public async generateImageAndDisplay(map: MapLibreMap, options: MapExportOption): Promise<void> {
        const mapElement = map.getContainer();

        if (!mapElement) {
            return;
        }

        const virtualClone = this.cloneMapHtmlElement(map, mapElement, options);

        const disabledSheets: HTMLLinkElement[] = this.getDisabledSheets();

        const rect = mapElement.getBoundingClientRect();
        const originalWidth = rect.width;
        const originalHeight = rect.height;

        try {
            this.imgUrl.set(
                await domtoimage.toPng(virtualClone, {
                    canvasHeight: originalHeight,
                    canvasWidth: originalWidth,
                    height: originalHeight,
                    skipFonts: true,
                    width: originalWidth,
                }),
            );

            virtualClone.remove();
            // eslint-disable-next-line no-useless-catch
        } catch (error: unknown) {
            throw error;
        } finally {
            disabledSheets.forEach((link: HTMLLinkElement) => (link.disabled = false));
        }
    }

    public getCompassImage(dataUrl: string, bearingDeg: number): Promise<string> {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.crossOrigin = 'anonymous';

            img.onload = (): void => {
                const srcW = img.naturalWidth || img.width;
                const srcH = img.naturalHeight || img.height;
                const rotationAngle = (bearingDeg * Math.PI) / -180;

                const cos = Math.abs(Math.cos(rotationAngle));
                const sin = Math.abs(Math.sin(rotationAngle));
                const rotW = srcW * cos + srcH * sin;
                const rotH = srcW * sin + srcH * cos;

                const canvas = document.createElement('canvas');

                const cw = Math.round(rotW);
                const ch = Math.round(rotH);

                canvas.width = cw;
                canvas.height = ch;

                const ctx = canvas.getContext('2d');
                if (!ctx) {
                    return reject();
                }

                ctx.imageSmoothingEnabled = true;
                ctx.imageSmoothingQuality = 'high';

                ctx.translate(cw / 2, ch / 2);
                ctx.rotate(rotationAngle);

                ctx.drawImage(img, -srcW / 2, -srcH / 2);

                const png = canvas.toDataURL('image/png');

                resolve(png);
            };

            img.onerror = reject;
            img.src = dataUrl;
        });
    }

    public async getHtmlElementToPng(
        element: Element | HTMLElement,
    ): Promise<{ dataUrl: string; height: number; width: number }> {
        const rect = element.getBoundingClientRect();
        const contentW = Math.ceil(rect.width);
        const contentH = Math.ceil(rect.height);

        const disabledSheets: HTMLLinkElement[] = this.getDisabledSheets();

        const dataUrl: string = await domtoimage.toPng(element, {
            bgcolor: 'rgb(255, 255, 255, 0.3)',
            height: contentH,
            style: {
                height: `${contentH}px`,
                overflow: 'visible',
                position: 'fixed',
                top: '0',
                width: `${contentW}px`,
            },
            width: contentW,
        });

        disabledSheets.forEach((link: HTMLLinkElement) => (link.disabled = false));

        return { dataUrl: dataUrl, height: contentH, width: contentW };
    }

    public setControlButtonIcon(): void {
        this.button.innerHTML = icon(faPrint, {
            styles: {
                'background-color': this.getIconBackgroundColor(),
                'border-radius': '0.2rem',
                color: this.getIconColor(),
                height: '1.25rem',
                padding: '0.25rem',
                width: '1.25rem',
            },
        }).html[0];
    }

    private cloneMapHtmlElement(map: MapLibreMap, mapElement: HTMLElement, options: MapExportOption): HTMLElement {
        const originalBounds = map.getBounds();

        const originalCanvas = map.getCanvas();

        const virtualClone = mapElement.cloneNode(true) as HTMLElement;
        const mapZIndex = getComputedStyle(mapElement).zIndex;

        mapElement.style.zIndex = '0';

        virtualClone.style.zIndex = '0';
        virtualClone.style.height = `${mapElement.getBoundingClientRect().height}px`;
        virtualClone.style.width = `${mapElement.getBoundingClientRect().width}px`;
        virtualClone.setAttribute('id', 'virtual-map-container');
        this.removeElements(virtualClone, 'canvas.maplibregl-canvas');

        if (options.bbox) {
            map.fitBounds(options.bbox, { animate: false });
        }
        map.redraw();

        this.manageOverlayVisibility(map, options, virtualClone);

        const newCanvas = this.copyCanvas(originalCanvas);
        map.fitBounds(originalBounds, { animate: false });
        virtualClone.getElementsByClassName('maplibregl-canvas-container')[0].append(newCanvas);

        mapElement.append(virtualClone);
        mapElement.style.zIndex = mapZIndex;

        return virtualClone;
    }

    private convertAndResizePng(
        dataUrl: string,
        format: MapExportFormatEnum,
        printSizeInPixel: { height: number; width: number },
    ): Promise<{ dataUrl: string; ratio: number }> {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = (): void => {
                const srcW = img.naturalWidth || img.width;
                const srcH = img.naturalHeight || img.height;

                const ratio = Math.min(printSizeInPixel.width / srcW, printSizeInPixel.height / srcH);

                const canvas = document.createElement('canvas');
                canvas.width = printSizeInPixel.width;
                canvas.height = printSizeInPixel.height;
                const ctx = canvas.getContext('2d')!;
                ctx.imageSmoothingEnabled = true;
                ctx.imageSmoothingQuality = 'high';

                const dw = Math.round(srcW * ratio);
                const dh = Math.round(srcH * ratio);
                const dx = Math.round((canvas.width - dw) / 2);
                const dy = Math.round((canvas.height - dh) / 2);

                this.bottomPad.set(printSizeInPixel.height - dy - dh);
                this.leftPad.set(printSizeInPixel.width - dx - dw);

                if (format === MapExportFormatEnum.JPEG) {
                    ctx.fillStyle = '#fff';
                    ctx.fillRect(0, 0, canvas.width, canvas.height);
                }

                ctx.drawImage(img, 0, 0, srcW, srcH, dx, dy, dw, dh);

                const dataUrlOut = canvas.toDataURL(
                    `image/${format}`,
                    format === MapExportFormatEnum.JPEG ? 0.92 : undefined,
                );
                resolve({ dataUrl: dataUrlOut, ratio: ratio });
            };
            img.onerror = reject;
            img.src = dataUrl;
        });
    }

    private convertPngToSvg(pngDataUrl: string, fileName: string): Promise<void> {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.src = pngDataUrl;

            img.onload = (): void => {
                const canvas = document.createElement('canvas');
                canvas.width = img.width;
                canvas.height = img.height;
                const ctx = canvas.getContext('2d');

                if (!ctx) {
                    return reject(new Error('Impossible to get canvas context'));
                }

                ctx.drawImage(img, 0, 0);

                const svgString: string = ImageTracer.imagedataToSVG(
                    ctx.getImageData(0, 0, canvas.width, canvas.height),
                    DEFAULT_SVG_OPTION,
                );

                this.downloadSvg(svgString, fileName).then(resolve).catch(reject);
            };

            img.onerror = reject;
        });
    }

    private copyCanvas(canvas: HTMLCanvasElement): HTMLElement {
        const newCanvas = document.createElement('canvas');
        const rect = canvas.getBoundingClientRect();

        newCanvas.width = rect.width;
        newCanvas.height = rect.height;
        newCanvas.style.width = String(rect.width);
        newCanvas.style.height = String(rect.height);

        const context = newCanvas.getContext('2d');

        if (!context) {
            return canvas;
        }

        context.drawImage(canvas, 0, 0, rect.width, rect.height);

        return newCanvas;
    }

    private createFinalCanvasContext(
        canvas: HTMLCanvasElement,
        printSizeInPixel: { height: number; width: number },
    ): CanvasRenderingContext2D {
        const dpr = window.devicePixelRatio || 1;
        canvas.width = Math.round(printSizeInPixel.width * dpr);
        canvas.height = Math.round(printSizeInPixel.height * dpr);
        canvas.style.width = `${printSizeInPixel.width}px`;
        canvas.style.height = `${printSizeInPixel.height}px`;

        const ctx = canvas.getContext('2d')!;
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';

        return ctx;
    }

    private cropAndDrawImage(
        ctx: CanvasRenderingContext2D,
        img: HTMLImageElement,
        imageInformation: ImageInformation,
    ): void {
        const srcW = img.naturalWidth || img.width;
        const srcH = img.naturalHeight || img.height;
        const boxW = imageInformation.w ?? srcW;
        const boxH = imageInformation.h ?? srcH;

        const base = boxW / srcW;
        const scale = base * SHRINK_LEGEND;
        const drawW = srcW * scale;
        const drawH = srcH * scale;

        ctx.save();
        ctx.beginPath();
        ctx.rect(imageInformation?.x ?? this.xLegendPlacement(), imageInformation.y, boxW, boxH);
        ctx.clip();

        ctx.drawImage(
            img,
            imageInformation?.x ?? this.xLegendPlacement() + this.leftPad(),
            imageInformation.y,
            drawW,
            drawH,
        );

        this.xLegendPlacement.update((value: number) => {
            if (imageInformation.x === undefined) {
                return value + drawW;
            }

            return value;
        });
    }

    private downloadImage(dataUrl: string, filename: string): void {
        const link = document.createElement('a');
        link.href = dataUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }

    private downloadSvg(svgString: string, fileName: string): Promise<void> {
        return new Promise((resolve) => {
            const blob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = fileName;
            document.body.appendChild(link);

            link.addEventListener('click', () => {
                setTimeout(() => {
                    document.body.removeChild(link);
                    URL.revokeObjectURL(url);
                    resolve();
                }, 100);
            });

            link.click();
        });
    }

    private async drawImagesOnContext(
        ctx: CanvasRenderingContext2D,
        imageInformations: ImageInformation[],
    ): Promise<void> {
        for (const imageInformation of imageInformations) {
            const img = await this.loadImage(imageInformation.dataUrl);

            if (!imageInformation.needCrop) {
                ctx.drawImage(
                    img,
                    imageInformation?.x ?? this.xLegendPlacement() + this.leftPad(),
                    imageInformation.y,
                    imageInformation.w,
                    imageInformation.h,
                );

                this.xLegendPlacement.update((value: number) => {
                    if (imageInformation.x === undefined) {
                        return value + imageInformation.w;
                    }

                    return value;
                });

                continue;
            }

            this.cropAndDrawImage(ctx, img, imageInformation);

            ctx.restore();
        }
    }

    private async generateFinalImage(
        map: MapLibreMap,
        bearing: number,
        format: MapExportFormatEnum,
        printSize: MapExportPrintSizeEnum,
        selectedOptions: SelectedMapExportOption,
    ): Promise<string> {
        const printSizeInPixel = PAPER_SIZE_IN_PIXEL[printSize];

        const { dataUrl: mapDataUrl, ratio: ratio } = await this.convertAndResizePng(
            this.imgUrl()!,
            format,
            printSizeInPixel,
        );

        const imageInformation: ImageInformation[] = [
            {
                dataUrl: mapDataUrl,
                h: printSizeInPixel.height,
                w: printSizeInPixel.width,
                x: 0,
                y: 0,
            },
        ];

        if (selectedOptions.legend) {
            const legendInformation = await this.getLegendPng(printSizeInPixel);

            if (legendInformation) {
                imageInformation.push(legendInformation);
            }
        }

        if (selectedOptions.scale) {
            const scaleInformation = await this.getScalePng(printSizeInPixel, map, ratio);

            if (scaleInformation) {
                imageInformation.push(scaleInformation);
            }
        }

        if (selectedOptions.compass) {
            const compassDataUrl = await this.getCompassImage('./assets/images/map/compass-rose.png', bearing);

            imageInformation.push({
                dataUrl: compassDataUrl,
                h: printSizeInPixel.height / 18,
                w: printSizeInPixel.height / 18,
                x: printSizeInPixel.width - printSizeInPixel.height / 18 - this.leftPad(),
                y: this.bottomPad(),
            });
        }

        return this.mergeImages(imageInformation, printSizeInPixel);
    }

    private getDisabledSheets(): HTMLLinkElement[] {
        const disabledSheets: HTMLLinkElement[] = [];
        Array.from(document.querySelectorAll('link[rel="stylesheet"]') as NodeListOf<HTMLLinkElement>).forEach(
            (link: HTMLLinkElement) => {
                const href = link.getAttribute('href') || '';

                if (
                    !href.includes('fonts.googleapis.com') &&
                    !href.includes('fonts.gstatic.com') &&
                    !href.includes('material') &&
                    !href.includes('cdn')
                ) {
                    return;
                }

                link.disabled = true;
                disabledSheets.push(link);
            },
        );

        return disabledSheets;
    }

    private getIconBackgroundColor(): string {
        return this.isMapExportActive()
            ? getComputedStyle(document.documentElement).getPropertyValue('--custom-light-blue').trim()
            : '';
    }

    private getIconColor(): string {
        if (!this.isMapExportActive()) {
            return '';
        }

        return this.isMapExportControlDisabled()
            ? getComputedStyle(document.documentElement).getPropertyValue('--custom-dark-grey').trim()
            : getComputedStyle(document.documentElement).getPropertyValue('--secondary').trim();
    }

    private async getLegendPng(printSizeInPixel: {
        height: number;
        width: number;
    }): Promise<ImageInformation | undefined> {
        const legendListElement = document.getElementById('legend-list');

        if (!legendListElement) {
            return undefined;
        }

        const legendListInfo = await this.getHtmlElementToPng(legendListElement);

        return {
            dataUrl: legendListInfo.dataUrl,
            h: printSizeInPixel.height - this.bottomPad() * 2,
            needCrop: true,
            w: printSizeInPixel.width / 4,
            y: this.bottomPad(),
        };
    }

    private async getScaleElementInformation(
        scaleElement: Element,
        height: number,
        ratio: number,
    ): Promise<ImageInformation> {
        const rect = scaleElement.getBoundingClientRect();
        const contentW = Math.ceil(rect.width);
        const contentH = Math.ceil(rect.height);

        const frame = document.createElement('div');
        frame.style.position = 'fixed';
        frame.style.top = '0';
        frame.style.width = `${contentW}px`;
        frame.style.height = `${contentH}px`;

        const cloneScaleElement = scaleElement.cloneNode(true) as HTMLElement;
        cloneScaleElement.style.width = `${contentW}px`;
        cloneScaleElement.style.height = `${contentH}px`;

        frame.appendChild(cloneScaleElement);
        document.body.appendChild(frame);

        const mapScaleInfo = await this.getHtmlElementToPng(frame);
        document.body.removeChild(frame);

        return {
            dataUrl: mapScaleInfo.dataUrl,
            h: mapScaleInfo.height * ratio,
            w: mapScaleInfo.width * ratio,
            y: height - this.bottomPad() - mapScaleInfo.height * ratio,
        };
    }

    private async getScalePng(
        printSizeInPixel: {
            height: number;
            width: number;
        },
        map: MapLibreMap,
        ratio: number,
    ): Promise<ImageInformation | undefined> {
        const scale = new ScaleControl({
            maxWidth: 80,
            unit: 'metric',
        });
        map.addControl(scale);

        const scaleElement: Element | null = document.getElementsByClassName('maplibregl-ctrl-scale').item(0);

        let mapScaleInformation: ImageInformation | undefined = undefined;

        if (scaleElement) {
            mapScaleInformation = await this.getScaleElementInformation(scaleElement, printSizeInPixel.height, ratio);
        }

        map.removeControl(scale);

        return mapScaleInformation;
    }

    private loadImage(url: string): Promise<HTMLImageElement> {
        return new Promise((resolve, rej) => {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = (): void => resolve(img);
            img.onerror = rej;
            img.src = url;
        });
    }

    private manageOverlayVisibility(map: MapLibreMap, options: MapExportOption, virtualClone: HTMLElement): void {
        if (options.hideAllControls) {
            this.removeElements(virtualClone, '.maplibregl-control-container');
        }

        if (options.hideMarkers) {
            this.removeElements(virtualClone, '.maplibregl-marker');
        }

        if (options.hidePopups) {
            this.removeElements(virtualClone, '.maplibregl-popup');
        }

        if (options.bbox) {
            map.fitBounds(options.bbox, { padding: 20 });
        }
    }

    private async mergeImages(
        imageInformations: ImageInformation[],
        printSizeInPixel: { height: number; width: number },
    ): Promise<string> {
        const canvas = document.createElement('canvas');
        const ctx = this.createFinalCanvasContext(canvas, printSizeInPixel);

        await this.drawImagesOnContext(ctx, imageInformations);

        return canvas.toDataURL('image/png');
    }

    private removeElements(element: HTMLElement, selector: string): void {
        Array.from(element.querySelectorAll(selector)).forEach((el: Element) => el.remove());
    }
}
